import React, { memo } from 'react';
import ImageWithFallback from './ImageWithFallback';
import { getLocalizedValue } from '../config/languageConfig';
import { SupportedCurrency, getPriceFieldByCurrency, formatPrice } from '../utils/currencyUtils';
import useTranslation from '../hooks/useTranslation';

interface CartItemProps {
  item: {
    id: string;
    name: string;
    nameEn?: string;
    nameJa?: string;
    nameZhCn?: string;
    nameZhTw?: string;
    image?: string | null;
    quantity: number;
    price: number;
    priceUsd?: number;
    priceJpy?: number;
    priceCny?: number;
    priceTwd?: number;
    options: Array<{ id: string }>;
  };
  currentLang: string;
  selectedCurrency: SupportedCurrency;
  onIncrease: (itemId: string, currentQuantity: number) => void;
  onDecrease: (itemId: string, currentQuantity: number) => void;
  onRemove: (itemId: string) => void;
}

const CartItem: React.FC<CartItemProps> = memo(({
  item,
  currentLang,
  selectedCurrency,
  onIncrease,
  onDecrease,
  onRemove
}) => {
  const { t } = useTranslation();

  // 현재 통화에 맞는 아이템 가격 반환
  const getItemPrice = (item: CartItemProps['item']) => {
    // 중국어 사용자의 경우 CNY 가격 표시 (메뉴 카드 내에서)
    const displayCurrency = currentLang === 'zh-CN' ? 'CNY' : selectedCurrency;
    const priceField = getPriceFieldByCurrency(displayCurrency);
    return item[priceField as keyof typeof item] !== undefined 
      ? item[priceField as keyof typeof item] as number
      : item.price;
  };

  // 가격 포맷팅
  const formatItemPrice = (price: number) => {
    // 중국어 사용자의 경우 CNY로 표시
    const displayCurrency = currentLang === 'zh-CN' ? 'CNY' : selectedCurrency;
    return formatPrice(price, displayCurrency);
  };

  // 현재 언어에 맞는 아이템 이름 반환
  const getItemName = () => {
    const nameField = `name${currentLang === 'en' ? 'En' : 
                    currentLang === 'ja' ? 'Ja' : 
                    currentLang === 'zh-CN' ? 'ZhCn' : 
                    currentLang === 'zh-TW' ? 'ZhTw' : ''}` as keyof typeof item;
    
    return item[nameField] !== undefined 
      ? String(item[nameField]) 
      : getLocalizedValue(item.name, {
          en: item.nameEn as string, 
          ja: item.nameJa as string,
          'zh-CN': item.nameZhCn as string,
          'zh-TW': item.nameZhTw as string
        });
  };

  return (
    <div className="py-4 flex items-start">
      {/* 메뉴 이미지 */}
      <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0 bg-gray-100">
        <ImageWithFallback
          src={item.image || ''}
          alt={item.name}
          className="w-full h-full object-cover"
          fallback="https://source.unsplash.com/random/100x100/?food"
        />
      </div>

      {/* 메뉴 정보 */}
      <div className="ml-3 flex-grow min-w-0">
        <div className="flex justify-between items-start">
          <h3 className="text-lg font-medium text-gray-900 truncate pr-2">
            {getItemName()}
          </h3>
          <button
            type="button"
            className="text-gray-400 hover:text-red-500 flex-shrink-0 ml-1"
            onClick={() => onRemove(item.id)}
            aria-label={`${t('cart.remove')} ${item.name}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* 가격과 수량 컨트롤 */}
        <div className="flex items-center justify-between mt-2">
          {/* 가격 표시 - 좌측 */}
          <div className="text-gray-600 text-base">
            {formatItemPrice(getItemPrice(item))}
            <span className="text-xs ml-1">{t('cart.perPiece')}</span>
          </div>
          
          {/* 수량 컨트롤 - 우측 */}
          <div className="flex items-center">
            <button
              type="button"
              className="w-8 h-8 flex items-center justify-center border rounded-md border-gray-300 text-gray-700"
              onClick={() => onDecrease(item.id, item.quantity)}
              aria-label={`${t('cart.decrease')} ${item.name}`}
              disabled={item.quantity <= 1}
            >
              <span className="text-lg font-medium">−</span>
            </button>
            <span className="mx-4 text-base font-medium">{item.quantity}</span>
            <button
              type="button"
              className="w-8 h-8 flex items-center justify-center border rounded-md bg-red-500 text-white border-red-500"
              onClick={() => onIncrease(item.id, item.quantity)}
              aria-label={`${t('cart.increase')} ${item.name}`}
            >
              <span className="text-lg font-medium">+</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

CartItem.displayName = 'CartItem';

export default CartItem; 